import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import classNames from 'classnames'

import cls from './accordion.module.scss'
import { Button } from '@/shared/ui/buttons/Button/Button'
import Text from '@/shared/ui/Text/Text'
import { MaterialIcon } from '@/shared/ui/MaterialIcon/MaterialIcon'

const AccordionContext = createContext(null)
const ItemContext = createContext(null)

const useAccordionContext = () => {
  const ctx = useContext(AccordionContext)
  if (!ctx) {
    throw new Error('Accordion.* components must be used within <Accordion>')
  }
  return ctx
}

const useItemContext = () => {
  const ctx = useContext(ItemContext)
  if (!ctx) {
    throw new Error('Accordion.Item.* components must be used within <Accordion.Item>')
  }
  return ctx
}

/**
 * Compound Accordion.
 *
 * @example
 * <Accordion type="single" defaultValue="a">
 *   <Accordion.Item value="a">
 *     <Accordion.Header>
 *       <Accordion.TitleBlock>
 *         <Accordion.Title>Title</Accordion.Title>
 *         <Accordion.Subtitle>Subtitle</Accordion.Subtitle>
 *       </Accordion.TitleBlock>
 *       <Accordion.Trigger />
 *     </Accordion.Header>
 *     <Accordion.Content>Body</Accordion.Content>
 *   </Accordion.Item>
 * </Accordion>
 */
export const Accordion = ({
  children,
  className,
  type = 'single',
  defaultValue,
  value: valueProp,
  onValueChange,
  onToggle,
  disabled = false,
}) => {
  const [uncontrolledValue, setUncontrolledValue] = useState(
    defaultValue !== undefined ? defaultValue : type === 'multiple' ? [] : null,
  )

  const value = valueProp !== undefined ? valueProp : uncontrolledValue

  const setValue = useCallback(
    next => {
      if (valueProp === undefined) {
        setUncontrolledValue(next)
      }
      onValueChange?.(next)
      onToggle?.()
    },
    [valueProp, onValueChange, onToggle],
  )

  const isOpen = useCallback(
    id => (type === 'multiple' ? Boolean(value?.includes?.(id)) : value === id),
    [type, value],
  )

  const toggle = useCallback(
    id => {
      if (disabled) return

      if (type === 'multiple') {
        const current = Array.isArray(value) ? value : []
        setValue(current.includes(id) ? current.filter(item => item !== id) : [...current, id])
        return
      }

      setValue(value === id ? null : id)
    },
    [disabled, type, value, setValue],
  )

  const contextValue = useMemo(
    () => ({
      isOpen,
      toggle,
      disabled,
    }),
    [isOpen, toggle, disabled],
  )

  return (
    <AccordionContext.Provider value={contextValue}>
      <div className={classNames(cls.accordion, className, { [cls.disabled]: disabled })}>
        {children}
      </div>
    </AccordionContext.Provider>
  )
}

const Item = ({ value, children, className }) => {
  const { isOpen } = useAccordionContext()
  const open = isOpen(value)

  const contextValue = useMemo(
    () => ({
      value,
      open,
    }),
    [value, open],
  )

  return (
    <ItemContext.Provider value={contextValue}>
      <div className={className} data-state={open ? 'open' : 'closed'}>
        {children}
      </div>
    </ItemContext.Provider>
  )
}

const Header = ({ children, className }) => (
  <div className={classNames(cls.container, className)}>{children}</div>
)

const TitleBlock = ({ children, className }) => (
  <div className={classNames(cls.titleContainer, className)}>{children}</div>
)

const Title = ({ children, className }) => (
  <Text className={classNames(cls.title, className)}>{children}</Text>
)

const Subtitle = ({ children, className }) => {
  if (children == null || children === false) return null
  return <Text className={classNames(cls.subtitle, className)}>{children}</Text>
}

const Icon = props => {
  const {
    name = 'arrow_back_ios',
    size = 24,
    ariaLabel = 'Развернуть',
    className,
    classNameExpanded,
  } = props
  const { open } = useItemContext()

  return (
    <MaterialIcon
      name={name}
      ariaLabel={ariaLabel}
      containerClassName={classNames(
        cls.icon,
        className,
        { [cls.iconExpanded]: open },
        { [classNameExpanded]: open },
      )}
      size={size}
      withContainer
    />
  )
}

const Trigger = ({ children, containerClassName, className, ...props }) => {
  const { toggle, disabled } = useAccordionContext()
  const { value, open } = useItemContext()

  return (
    <div className={classNames(cls.triggerContainer, containerClassName)}>
      <Button
        className={classNames(cls.trigger, className)}
        type="button"
        onClick={() => toggle(value)}
        aria-expanded={open}
        disabled={disabled}
        {...props}
      >
        {children ?? (
          <>
            <Text className={cls.buttonText}>Показать список</Text>
            <Icon />
          </>
        )}
      </Button>
    </div>
  )
}

const Content = ({ children, className, classNameExpanded }) => {
  const { open } = useItemContext()

  return (
    <div
      className={classNames(
        cls.content,
        className,
        { [cls.contentExpanded]: open },
        { [classNameExpanded]: open },
      )}
    >
      <div className={cls.contentInner}>{children}</div>
    </div>
  )
}

Accordion.Item = Item
Accordion.Header = Header
Accordion.TitleBlock = TitleBlock
Accordion.Title = Title
Accordion.Subtitle = Subtitle
Accordion.Trigger = Trigger
Accordion.Icon = Icon
Accordion.Content = Content
